import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import { InvTextField, InvSelect, DetailRow } from '../../components/InvField';
import { createReplacementGrn } from '../../api/qcRejections';
import { formatQty, generateTxnNumber } from '../../utils';
import {
  PAYMENT_TYPE_OPTIONS,
  buildReplacementGrnBody,
  formatReplacementGrnError,
  validateReplacementGrnForm,
} from '../../utils/replacementGrnEntity';
import { IPD_COLORS } from '../../theme';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function initialForm(context) {
  return {
    grn_number: generateTxnNumber('GRN-REP'),
    received_quantity: '',
    batch_number: '',
    manufacturer_batch_number: '',
    manufacturing_date: '',
    expiry_date: '',
    mrp: '',
    purchase_rate: '',
    sale_rate: '',
    tax_percent: '',
    free_quantity: '',
    discount_percent: '',
    discount_amount: '',
    invoice_number: '',
    invoice_date: '',
    delivery_challan_number: '',
    delivery_date: '',
    grn_date: todayIso(),
    payment_type: '',
    gate_entry_number: '',
    gate_entry_date: '',
    place_of_supply: '',
    external_po_reference: '',
    remarks: context?.grn_number ? `Replacement for ${context.grn_number}` : '',
  };
}

function isFormDirty(form, baseline) {
  return Object.keys(baseline).some((key) => String(form[key] ?? '') !== String(baseline[key] ?? ''));
}

export default function ReplacementGrnForm() {
  const history = useHistory();
  const location = useLocation();
  const { grnItemId } = useParams();
  const { url } = useRouteMatch();
  const base = url.replace(/\/rejected-items\/replacement-grn\/.*$/, '');

  const context = location.state?.context || null;
  const pendingQty = context != null ? Number(context.pending_replacement_qty) || 0 : null;

  const initialSnapshot = useRef(initialForm(context));
  const [form, setForm] = useState(() => initialSnapshot.current);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [created, setCreated] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    if (!context) {
      toast.info('Open this form from the Rejected Items dashboard.');
      history.replace(`${base}/rejected-items`);
    }
  }, [context, history, base]);

  function update(key, value) {
    setForm(f => ({ ...f, [key]: value }));
    setFieldErrors(fe => (fe[key] ? { ...fe, [key]: undefined } : fe));
  }

  const qtyExceeds = pendingQty != null && Number(form.received_quantity) > pendingQty;
  const submitDisabled = saving
    || !context
    || !form.grn_number.trim()
    || !(Number(form.received_quantity) > 0)
    || qtyExceeds;

  const handleCancel = useCallback(() => {
    if (isFormDirty(form, initialSnapshot.current)) {
      setCancelOpen(true);
      return;
    }
    history.push(`${base}/rejected-items`);
  }, [form, history, base]);

  async function handleSubmit() {
    const err = validateReplacementGrnForm(form, { pendingQty });
    if (err) {
      setFieldErrors(err.field ? { [err.field]: err.message } : {});
      setFormError(err.field ? null : err.message);
      return;
    }
    setFormError(null);
    setFieldErrors({});
    setSaving(true);
    try {
      const body = buildReplacementGrnBody(form);
      const result = await createReplacementGrn(grnItemId, body);
      toast.success(`Replacement GRN ${form.grn_number} created successfully`);
      setCreated(result || {});
    } catch (e) {
      const { field, message, shouldCloseForm } = formatReplacementGrnError(e);
      if (field) setFieldErrors({ [field]: message });
      else setFormError(message);
      if (shouldCloseForm) {
        toast.error(message);
        setTimeout(() => history.replace(`${base}/rejected-items`), 1500);
      }
    } finally {
      setSaving(false);
    }
  }

  if (!context) {
    return null;
  }

  if (created) {
    const newId = created?.grn_id || created?.id;
    return (
      <Box>
        <SectionCard title="">
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700, mb: 1 }}>
              Replacement GRN Created
            </Typography>
            <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 3 }}>
              {form.grn_number} has been recorded as a <strong>DRAFT</strong> replacement
              {context?.grn_number ? <> for GRN {context.grn_number}</> : null}. Receive, QC, and
              post it to stock like any other GRN.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <InvButton variant="outlined" tone="teal" onClick={() => history.push(`${base}/rejected-items`)}>
                Back to Dashboard
              </InvButton>
              {newId && (
                <InvButton tone="mint" onClick={() => history.push(`${base}/grn/${newId}`)}>
                  View GRN
                </InvButton>
              )}
            </Box>
          </Box>
        </SectionCard>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management › Rejected Items"
        title="Create Replacement GRN"
        actions={<InvButton variant="outlined" tone="teal" size="small" onClick={handleCancel}>Cancel</InvButton>}
      />

      <SectionCard title="Source Rejection" sx={{ mb: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}><DetailRow label="Source GRN">{context.grn_number || '—'}</DetailRow></Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DetailRow label="PO">{context.po_number || context.purchase_order_id || '—'}</DetailRow>
          </Grid>
          <Grid item xs={12} sm={6} md={4}><DetailRow label="Item">{context.item_name || context.item_id || '—'}</DetailRow></Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DetailRow label="Rejected Qty">{formatQty(context.rejected_quantity)}</DetailRow>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DetailRow label="Pending Qty (max replaceable)">{formatQty(pendingQty)}</DetailRow>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DetailRow label="Rejection Reason">{context.rejection_reason || '—'}</DetailRow>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Replacement GRN Details" sx={{ mb: 2.5 }}>
        <Grid container spacing={2} sx={{ maxWidth: 720 }}>
          <Grid item xs={12} sm={6}>
            <InvTextField
              label="Replacement GRN Number *"
              value={form.grn_number}
              error={Boolean(fieldErrors.grn_number)}
              helperText={fieldErrors.grn_number || 'Auto-generated; change only if needed'}
              onChange={e => update('grn_number', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InvTextField
              label="Received Quantity *"
              type="number"
              inputProps={{ min: 1, max: pendingQty, step: 1 }}
              value={form.received_quantity}
              error={Boolean(fieldErrors.received_quantity) || qtyExceeds}
              helperText={
                fieldErrors.received_quantity
                || (qtyExceeds ? `Cannot exceed pending quantity (${formatQty(pendingQty)})` : undefined)
              }
              onChange={e => update('received_quantity', e.target.value)}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Batch Details" subtitle="Optional — recommended for batch-tracked items" sx={{ mb: 2.5 }}>
        <Grid container spacing={2} sx={{ maxWidth: 960 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Batch Number" value={form.batch_number} onChange={e => update('batch_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Manufacturer Batch Number" value={form.manufacturer_batch_number}
              onChange={e => update('manufacturer_batch_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Manufacturing Date" type="date" InputLabelProps={{ shrink: true }}
              value={form.manufacturing_date} onChange={e => update('manufacturing_date', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Expiry Date" type="date" InputLabelProps={{ shrink: true }}
              value={form.expiry_date}
              error={Boolean(fieldErrors.expiry_date)}
              helperText={fieldErrors.expiry_date}
              onChange={e => update('expiry_date', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="MRP" type="number" inputProps={{ min: 0, step: '0.01' }}
              value={form.mrp} onChange={e => update('mrp', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Purchase Rate" type="number" inputProps={{ min: 0, step: '0.01' }}
              value={form.purchase_rate} onChange={e => update('purchase_rate', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Sale Rate" type="number" inputProps={{ min: 0, step: '0.01' }}
              value={form.sale_rate} onChange={e => update('sale_rate', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Tax %" type="number" inputProps={{ min: 0, max: 100, step: '0.01' }}
              value={form.tax_percent} onChange={e => update('tax_percent', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Free Quantity" type="number" inputProps={{ min: 0, step: 1 }}
              value={form.free_quantity} onChange={e => update('free_quantity', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Discount %" type="number" inputProps={{ min: 0, max: 100, step: '0.01' }}
              value={form.discount_percent} onChange={e => update('discount_percent', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Discount Amount" type="number" inputProps={{ min: 0, step: '0.01' }}
              value={form.discount_amount} onChange={e => update('discount_amount', e.target.value)} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Invoice / Delivery Details" subtitle="Optional" sx={{ mb: 2.5 }}>
        <Grid container spacing={2} sx={{ maxWidth: 960 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Invoice Number" value={form.invoice_number} onChange={e => update('invoice_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Invoice Date" type="date" InputLabelProps={{ shrink: true }}
              value={form.invoice_date} onChange={e => update('invoice_date', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvSelect label="Payment Type" value={form.payment_type} options={PAYMENT_TYPE_OPTIONS}
              onChange={e => update('payment_type', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Delivery Challan Number" value={form.delivery_challan_number}
              onChange={e => update('delivery_challan_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Delivery Date" type="date" InputLabelProps={{ shrink: true }}
              value={form.delivery_date} onChange={e => update('delivery_date', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="GRN Date" type="date" InputLabelProps={{ shrink: true }}
              value={form.grn_date} onChange={e => update('grn_date', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Gate Entry Number" value={form.gate_entry_number}
              onChange={e => update('gate_entry_number', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Gate Entry Date" type="date" InputLabelProps={{ shrink: true }}
              value={form.gate_entry_date} onChange={e => update('gate_entry_date', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="Place of Supply" value={form.place_of_supply}
              onChange={e => update('place_of_supply', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InvTextField label="External PO Reference" value={form.external_po_reference}
              onChange={e => update('external_po_reference', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <InvTextField label="Remarks" multiline minRows={2} value={form.remarks}
              onChange={e => update('remarks', e.target.value)} />
          </Grid>
        </Grid>
      </SectionCard>

      {formError && (
        <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 13, mb: 2 }}>{formError}</Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <InvButton variant="outlined" tone="teal" onClick={handleCancel} disabled={saving}>
          Cancel
        </InvButton>
        <InvButton loading={saving} disabled={submitDisabled} onClick={handleSubmit}>
          Create Replacement GRN
        </InvButton>
      </Box>

      <ConfirmDialog
        open={cancelOpen}
        title="Discard changes?"
        description="You have unsaved changes on this replacement GRN. Leave without saving?"
        destructive
        onConfirm={() => history.push(`${base}/rejected-items`)}
        onCancel={() => setCancelOpen(false)}
      />
    </Box>
  );
}
